import React, { useState, useEffect } from 'react';
import {
    Box,
    CircularProgress,
    Grid,
    Typography
} from '@mui/material';
import { AddLink, Cancel, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Container, Paper } from '../StyledComponents';
import { Header, Popup } from '../CommonComponents';
import { createFeedback } from '../../api/feedback';
import toast from '../../utils/toast';
import { createIntegration, deleteIntegration } from '../../api/integrations';
import { useDispatch, useSelector } from '../../redux/hooks';
import {
    fetchAllIntegrations,
    selectAllIntegrations,
    fetchIntegrationById,
    selectIntegrationById
} from '../../redux/actions/integrations';
import parseIntegrationType, { LOGTRADE, LOGTRADE_STATUS } from './integrationTypes';
import { IntegrationForListType } from '../../types';
import { logtradeClientId, logtradeRequestingParty } from '../../env';

type IntegrationBlockProps = {
    title: string,
    // description: string,
    logoPath?: string,
    icon?: JSX.Element,
    // color: string,
    // backgroundColor: string,
    onClick: (name: string) => void
}

function IntegrationBlock({
    title,
    // description,
    logoPath,
    icon = <AddLink sx={{ fontSize: 96 }} color="primary" />,
    // color,
    // backgroundColor,
    onClick
}: IntegrationBlockProps) {
    return (
        <Paper
            sx={{
                textAlign: 'center',
                opacity: 0.7,
                // color,
                transition: 'opacity 0.3s ease',
                ':hover': {
                    cursor: 'pointer',
                    opacity: 1
                }
            }}
            onClick={() => onClick(title)}
        >
            <Box
                sx={{
                    height: 150,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {logoPath ? (
                    <Box
                        component="img"
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                        }}
                        alt="Logo"
                        src={logoPath}
                    />
                ) : (
                    icon
                )}
            </Box>
            <Typography variant="h6">
                {title}
            </Typography>
            {/* <Typography>
                {description}
            </Typography> */}
        </Paper>
    );
}

export default function IntegrationSignup() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [popupIsOpen, setPopupIsOpen] = React.useState(false);
    const [okDisabled, setOkDisabled] = React.useState(false);
    const [integrationName, setIntegrationName] = React.useState<string | undefined>(undefined);
    const [createdIntegrationId, setCreatedIntegrationId] = React.useState<number | undefined>(undefined);
    const [existingIntegrationId, setExistingIntegrationId] = React.useState<number | undefined>(undefined);
    const [existingActiveIntegration, setExistingActiveIntegration] = React.useState(false);
    const waitingForIntegrationConfirmationRef = React.useRef(false);
    const [timeLeft, setTimeLeft] = useState(300);

    const integrations = useSelector(selectAllIntegrations());

    const createdIntegration = useSelector(selectIntegrationById(createdIntegrationId));

    React.useEffect(() => {
        dispatch(fetchAllIntegrations());
    }, [dispatch]);

    React.useEffect(() => {
        if (!waitingForIntegrationConfirmationRef.current || !createdIntegrationId) {
            return undefined;
        }

        let isMounted = true;

        const pollStatus = () => {
            const doPoll = async () => {
                if (!isMounted || !waitingForIntegrationConfirmationRef.current) return;
                try {
                    await dispatch(fetchIntegrationById(createdIntegrationId));
                } catch (err) {
                    // Ignore polling errors, will retry
                }
                if (isMounted && waitingForIntegrationConfirmationRef.current) {
                    setTimeout(doPoll, 3000);
                }
            };
            doPoll();
        };

        pollStatus();

        return () => {
            isMounted = false;
        };
    }, [createdIntegrationId, dispatch]);

    React.useEffect(() => {
        if (createdIntegration?.settings?.status && createdIntegration.settings.status !== LOGTRADE_STATUS.pending) {
            waitingForIntegrationConfirmationRef.current = false;
            setOkDisabled(false);
            if (createdIntegration.settings.status === LOGTRADE_STATUS.complete) {
                toast('Integration godkänd!');
            } else if (createdIntegration.settings.status === LOGTRADE_STATUS.rejected) {
                toast('Integration nekad i Logtrade.');
            }
        }
        if (createdIntegration?.settings.status === LOGTRADE_STATUS.pending && createdIntegration.isExpired) {
            waitingForIntegrationConfirmationRef.current = false;
            setOkDisabled(false);
            toast('Integrationstiden har gått ut, försök igen.');
        }
    }, [createdIntegration, createdIntegrationId, navigate]);

    const onClick = (name: string) => {
        setIntegrationName(name);
        setPopupIsOpen(true);
    };

    const handleClose = () => {
        setPopupIsOpen(false);
        setIntegrationName(undefined);
        setCreatedIntegrationId(undefined);
        waitingForIntegrationConfirmationRef.current = false;
        dispatch(fetchAllIntegrations());
        setOkDisabled(false);
    };

    React.useEffect(() => {
        if (integrationName === 'Logtrade') {
            const existing = integrations
                .find((i: IntegrationForListType) => parseIntegrationType(i.type) === integrationName);
            setExistingActiveIntegration(existing?.status === LOGTRADE_STATUS.complete);
            setExistingIntegrationId(existing?.id);
        }
    }, [integrations, integrationName]);

    const okLabel = React.useCallback(() => {
        if (existingActiveIntegration) {
            return 'Redigera integration';
        }
        if (waitingForIntegrationConfirmationRef.current) {
            return 'Väntar...';
        }
        if (createdIntegration?.settings.status === LOGTRADE_STATUS.complete) {
            return 'Inställningar';
        }
        if (createdIntegration?.settings.status === LOGTRADE_STATUS.rejected) {
            return 'Tillbaka';
        }
        if (createdIntegration?.settings.status === LOGTRADE_STATUS.pending && createdIntegration.isExpired) {
            return 'Tillbaka';
        }
        return 'Skapa integration';
    }, [
        existingActiveIntegration,
        createdIntegration
    ]);

    const signup = React.useCallback(async () => {
        if (integrationName === 'Logtrade') {
            setOkDisabled(true);
            if (!existingActiveIntegration && existingIntegrationId) {
                try {
                    await deleteIntegration(existingIntegrationId);
                } catch (e: any) {
                    toast('Kunde inte skapa Logtrade-integration just nu, försök igen');
                    return;
                }
            }
            try {
                const response = await createIntegration({
                    type: LOGTRADE
                });
                if (response.success === true && response.state && response.integration.id) {
                    if (!logtradeClientId || !logtradeRequestingParty) {
                        toast('Logtrade-integration är inte korrekt konfigurerad, kontakta support.');
                        setOkDisabled(false);
                        return;
                    }
                    setTimeLeft(300);
                    toast('Integration påbörjad, du skickas nu vidare till Logtrade...');
                    const params = new URLSearchParams({
                        client_id: logtradeClientId,
                        state: response.state,
                        connection_name: 'Logic Link Connection',
                        requesting_party: logtradeRequestingParty
                    }).toString();
                    const redirectUrl = `https://iol.logtrade.org/oauth/connect?${params}`;
                    setCreatedIntegrationId(response.integration.id);
                    dispatch(fetchIntegrationById(response.integration.id));
                    waitingForIntegrationConfirmationRef.current = true;
                    window.open(redirectUrl, '_blank');
                }
            } catch (e: any) {
                toast('Kunde inte skapa Logtrade-integration just nu, försök igen');
            }
        } else {
            try {
                await createFeedback({
                    message: `Jag är intresserad av en integration mot ${integrationName}, vänligen kontakta mig.`
                });
                toast('Tack för din intresseanmälan, vi kommer att höra av oss inom kort!');
            } catch (e: any) {
                toast(e, 'Kunde inte skicka intresseanmälan just nu, försök igen');
            }
            setPopupIsOpen(false);
        }
    }, [
        integrationName,
        existingActiveIntegration,
        existingIntegrationId,
        dispatch
    ]);

    const handleOk = React.useCallback(() => {
        if (integrationName === 'Logtrade' && existingActiveIntegration && existingIntegrationId) {
            navigate(`/integrations/edit/${existingIntegrationId}`);
        } else if (createdIntegration?.settings?.status === LOGTRADE_STATUS.complete && createdIntegrationId) {
            navigate(`/integrations/edit/${createdIntegrationId}`);
        } else if (createdIntegration?.settings?.status === LOGTRADE_STATUS.rejected) {
            handleClose();
        } else if (createdIntegration?.settings?.status === LOGTRADE_STATUS.pending && createdIntegration.isExpired) {
            handleClose();
        } else {
            signup();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        navigate,
        signup,
        existingActiveIntegration,
        existingIntegrationId,
        integrationName,
        createdIntegration,
        createdIntegrationId
    ]);

    useEffect(() => {
        if (!waitingForIntegrationConfirmationRef.current || !createdIntegrationId) {
            return undefined;
        }

        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 0 || !waitingForIntegrationConfirmationRef.current) {
                    return prev;
                }
                return prev - 1;
            });
        }, 980);

        return () => clearInterval(timer);
    }, [createdIntegrationId]);

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <Container>
            <Popup
                open={popupIsOpen && integrationName !== 'Logtrade'}
                title={`Integration - ${integrationName}`}
                handleOk={handleOk}
                handleClose={handleClose}
                okLabel="Ja, kontakta mig"
            >
                <Typography sx={{ pb: 2 }}>
                    Genom en integration kan du lägga ut uppdrag i Logic Link direkt från ditt
                    transporthanteringssystem.
                </Typography>

                <Typography sx={{ pb: 2 }}>
                    {`Är du intresserad av en integration mot ${integrationName}?`}
                </Typography>
                <Typography>
                    I så fall kontaktar vi dig inom kort för att hjälpa dig igång.
                </Typography>
            </Popup>

            <Popup
                open={popupIsOpen && integrationName === 'Logtrade'}
                title={`Integration - ${integrationName}`}
                handleOk={() => handleOk()}
                handleClose={() => handleClose()}
                okLabel={okLabel()}
                okDisabled={okDisabled}
            >
                {(!existingActiveIntegration && !createdIntegration) && (
                    <>
                        <Typography sx={{ pb: 2 }}>
                            Genom en integration kan du lägga ut uppdrag i Logic Link direkt från ditt Logtrade-konto.
                        </Typography>
                        <Typography sx={{ pb: 2 }}>
                            Klicka nedan för att påbörja integrationen. Du skickas då till Logtrade för att godkänna kopplingen och återvänder sedan hit för att slutföra inställningarna.
                        </Typography>
                    </>
                )}
                {existingActiveIntegration && (
                    <>
                        <Typography sx={{ pb: 2 }}>
                            Du har redan en färdigkonfigurerad Logtrade-integration.
                            Tryck på knappen nedan för att redigera den.
                        </Typography>
                        <Typography sx={{ pb: 2 }}>
                            (Om du vill skapa en ny integration så måste den gamla först raderas)
                        </Typography>
                    </>
                )}
                {waitingForIntegrationConfirmationRef.current && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 2, pb: 2
                    }}
                    >
                        <CircularProgress size={24} />
                        <Box>
                            <Typography>
                                Inväntar godkännande av integrationen i Logtrade.
                            </Typography>
                            <Typography>
                                Tid kvar:&nbsp;
                                {formatTime(timeLeft)}
                            </Typography>
                        </Box>
                    </Box>
                )}
                {createdIntegration?.settings.status === LOGTRADE_STATUS.complete && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 2, pb: 2
                    }}
                    >
                        <CheckCircle color="success" sx={{ fontSize: 32 }} />
                        <Box>
                            <Typography sx={{ pb: 1 }}>
                                Integrationen är nu godkänd och upprättad.
                            </Typography>
                            <Typography>
                                Tryck på knappen nedan för att gå vidare till inställningar.
                            </Typography>
                        </Box>
                    </Box>
                )}
                {createdIntegration?.settings.status === LOGTRADE_STATUS.rejected && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 2, pb: 2
                    }}
                    >
                        <Cancel color="error" sx={{ fontSize: 32 }} />
                        <Box>
                            <Typography sx={{ pb: 1 }}>
                                Integrationen nekades i Logtrade.
                            </Typography>
                            <Typography>
                                Tryck på knappen nedan för att gå tillbaka.
                            </Typography>
                        </Box>
                    </Box>
                )}
                {(createdIntegration?.settings.status === LOGTRADE_STATUS.pending && createdIntegration?.isExpired) && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 2, pb: 2
                    }}
                    >
                        <Cancel color="error" sx={{ fontSize: 32 }} />
                        <Box>
                            <Typography sx={{ pb: 1 }}>
                                Tiden löpte ut för integrationen, försök igen.
                            </Typography>
                            <Typography>
                                Tryck på knappen nedan för att gå tillbaka.
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Popup>

            <Header title="Integrationer" />

            <Grid container spacing={4}>
                <Grid item xs={12} md={6} lg={3}>
                    <IntegrationBlock
                        title="Opter"
                        // description="Description..."
                        logoPath="/opter-logo.png"
                        onClick={onClick}
                    // color="#999"
                    // backgroundColor="#FE6"
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                    <IntegrationBlock
                        title="Barkfors T5"
                        // description="Description..."
                        logoPath="/barkfors-logo.png"
                        onClick={onClick}
                    // color="#999"
                    // backgroundColor="#FE6"
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                    <IntegrationBlock
                        title="Hogia"
                        // description="Description..."
                        logoPath="/hogia-logo.png"
                        onClick={onClick}
                    // color="#999"
                    // backgroundColor="#FE6"
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                    <IntegrationBlock
                        title="Workify"
                        // description="Description..."
                        logoPath="/workify-logo.png"
                        onClick={onClick}
                    // color="#999"
                    // backgroundColor="#FE6"
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    md={6}
                    lg={3}
                    display={logtradeClientId && logtradeRequestingParty ? 'block' : 'none'}
                >
                    <IntegrationBlock
                        title="Logtrade"
                        // description="Description..."
                        logoPath="/logtrade-logo.png"
                        onClick={onClick}
                    // color="#999"
                    // backgroundColor="#FE6"
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                    <IntegrationBlock
                        title="Eget system"
                        // description="Description..."
                        // icon={<AddLink />}
                        onClick={onClick}
                    // color="#999"
                    // backgroundColor="#FE6"
                    />
                </Grid>
            </Grid>
        </Container>
    );
}
